// API Error Types and Handlers

export interface ApiErrorResponse {
  error?: {
    code: number;
    message: string;
    details?: string;
    validationErrors?: Array<{
      message: string;
      members: string[];
    }>;
  };
  success: boolean;
  result?: any;
  unAuthorizedRequest?: boolean;
}

export class ApiError extends Error {
  public code: number;
  public details?: string;
  public validationErrors?: Array<{
    message: string;
    members: string[];
  }>;
  public isNetworkError: boolean;
  public isAuthError: boolean;
  public isValidationError: boolean;
  public originalError?: any;

  constructor(
    message: string,
    code: number = 0,
    details?: string,
    validationErrors?: any[],
    originalError?: any
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.validationErrors = validationErrors;
    this.originalError = originalError;
    
    // Set error type flags
    this.isNetworkError = code === -1 || message.toLowerCase().includes('network');
    this.isAuthError = code === 401 || code === 403;
    this.isValidationError = code === 400 || Boolean(validationErrors && validationErrors.length > 0);
  }

  // Get user-friendly error message
  getUserMessage(): string {
    if (this.isNetworkError) {
      return 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
    }

    if (this.isAuthError) {
      return 'Oturum süreniz dolmuş olabilir. Lütfen tekrar giriş yapın.';
    }

    if (this.isValidationError && this.validationErrors) {
      return this.validationErrors.map(err => err.message).join('\n');
    }

    // Common error messages
    const errorMessages: Record<number, string> = {
      400: 'Geçersiz istek. Lütfen bilgileri kontrol edin.',
      401: 'Bu işlem için yetkiniz bulunmamaktadır.',
      403: 'Bu içeriğe erişim izniniz yok.',
      404: 'Aradığınız içerik bulunamadı.',
      409: 'Bu işlem başka bir kullanıcı tarafından zaten gerçekleştirilmiş.',
      429: 'Çok fazla istek gönderdiniz. Lütfen biraz bekleyin.',
      500: 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.',
      502: 'Sunucuya ulaşılamıyor. Lütfen daha sonra tekrar deneyin.',
      503: 'Servis geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.',
    };

    return errorMessages[this.code] || this.message || 'Beklenmeyen bir hata oluştu.';
  }

  // Check if error is retryable
  isRetryable(): boolean {
    return this.isNetworkError || 
           this.code === 429 || 
           this.code === 502 || 
           this.code === 503 ||
           this.code >= 500;
  }
}

// Parse API error response
export function parseApiError(error: any): ApiError {
  // Network errors
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return new ApiError('Ağ bağlantı hatası', -1, 'Network connection failed', undefined, error);
  }

  // Timeout errors
  if (error.name === 'AbortError') {
    return new ApiError('İstek zaman aşımına uğradı', -2, 'Request timeout', undefined, error);
  }

  // API response errors
  if (error.response) {
    const data = error.response.data as ApiErrorResponse;
    
    if (data?.error) {
      return new ApiError(
        data.error.message || 'API Hatası',
        data.error.code || error.response.status,
        data.error.details,
        data.error.validationErrors,
        error
      );
    }

    // Generic HTTP errors
    return new ApiError(
      error.response.statusText || 'HTTP Hatası',
      error.response.status,
      undefined,
      undefined,
      error
    );
  }

  // Generic errors
  return new ApiError(
    error.message || 'Bilinmeyen hata',
    0,
    undefined,
    undefined,
    error
  );
}

// Error notification helper
export interface ErrorNotification {
  type: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    handler: () => void;
  };
}

export function createErrorNotification(error: ApiError): ErrorNotification {
  const notification: ErrorNotification = {
    type: 'error',
    title: 'Hata',
    message: error.getUserMessage(),
    duration: 5000
  };

  // Add retry action for retryable errors
  if (error.isRetryable()) {
    notification.action = {
      label: 'Tekrar Dene',
      handler: () => {
        // This will be implemented by the component using this
        console.log('Retry requested');
      }
    };
  }

  // Add login action for auth errors
  if (error.isAuthError) {
    notification.type = 'warning';
    notification.title = 'Oturum Hatası';
    notification.action = {
      label: 'Giriş Yap',
      handler: () => {
        window.location.href = '/auth';
      }
    };
  }

  return notification;
}

// Error logger for development
export function logApiError(error: ApiError, context?: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🚨 API Error${context ? ` - ${context}` : ''}`);
    console.error('Message:', error.message);
    console.error('Code:', error.code);
    console.error('Details:', error.details);
    console.error('Validation Errors:', error.validationErrors);
    console.error('Stack:', error.stack);
    console.error('Original Error:', error.originalError);
    console.groupEnd();
  }
}